import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../../nav_bar/Navbar";
import Footer from "../../footer/Footer";
import { fetchPhrases } from "./phraseService";
import "./PhraseListPage.css";

const TYPES = ["PHRASE", "ABBREVIATION", "IDIOM", "PHRASAL_VERB"];
const CATEGORIES = ["EVERYDAY", "OFFICE", "ACADEMIC", "BUSINESS", "MEDICAL", "LEGAL", "TECH", "FINANCE", "TRAVEL", "SOCIAL"];
const DIFFICULTIES = ["BEGINNER", "INTERMEDIATE", "ADVANCED"];

const formatLabel = (s) => s?.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) || s;

const difficultyColor = (d) => {
  const map = { BEGINNER: "#4caf50", INTERMEDIATE: "#ff9800", ADVANCED: "#e91e63" };
  return map[d?.toUpperCase()] || "#999";
};

const PhraseListPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [type, setType] = useState("");
  const [category, setCategory] = useState("");
  const [difficulty, setDifficulty] = useState("");
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    console.log("[PhraseListPage] Loading phrases, page:", page, "search:", search, "type:", type, "category:", category, "difficulty:", difficulty);
    setLoading(true);
    const res = await fetchPhrases({
      page, size: 20,
      search: search || undefined,
      type: type || undefined,
      category: category || undefined,
      difficulty: difficulty || undefined,
    });
    if (res.status === 200 && res.data?.status) {
      console.log("[PhraseListPage] Loaded", res.data.data?.length || 0, "phrases");
      setItems(res.data.data || []);
      setTotalPages(res.data.totalPages || 0);
      setTotalItems(res.data.totalItems || 0);
    } else {
      console.error("[PhraseListPage] Load failed:", res.status, res.message);
      setItems([]);
      setTotalPages(0);
      setTotalItems(0);
    }
    setLoading(false);
  }, [page, search, type, category, difficulty]);

  useEffect(() => { load(); }, [load]);

  const handleFilterChange = (setter) => (e) => {
    setPage(0);
    setter(e.target.value);
  };

  return (
    <>
      <Navbar />
      <div className="phrase-page">
        <h1 className="phrase-title">💬 Phrases & Abbreviations</h1>
        <p className="phrase-subtitle">Master everyday phrases, idioms, abbreviations and phrasal verbs.</p>

        <div className="phrase-filters">
          <input
            className="phrase-search" type="text" placeholder="Search phrases..."
            value={search} onChange={handleFilterChange(setSearch)}
          />
          <select className="phrase-filter-select" value={type} onChange={handleFilterChange(setType)}>
            <option value="">All Types</option>
            {TYPES.map((t) => <option key={t} value={t}>{formatLabel(t)}</option>)}
          </select>
          <select className="phrase-filter-select" value={category} onChange={handleFilterChange(setCategory)}>
            <option value="">All Categories</option>
            {CATEGORIES.map((c) => <option key={c} value={c}>{formatLabel(c)}</option>)}
          </select>
          <select className="phrase-filter-select" value={difficulty} onChange={handleFilterChange(setDifficulty)}>
            <option value="">All Levels</option>
            {DIFFICULTIES.map((d) => <option key={d} value={d}>{formatLabel(d)}</option>)}
          </select>
        </div>

        {totalItems > 0 && <p className="phrase-count">{totalItems} phrases found</p>}

        {loading ? (
          <p className="phrase-loading">Loading...</p>
        ) : items.length === 0 ? (
          <p className="phrase-empty">No phrases found.</p>
        ) : (
          <div className="phrase-grid">
            {items.map((p) => (
              <div key={p.id} className="phrase-card" onClick={() => navigate(`/study/phrases/${p.id}`)}>
                <div className="phrase-card-header">
                  <div>
                    <h3 className="phrase-card-title">{p.title}</h3>
                    {p.fullForm && <div className="phrase-card-fullform">{p.fullForm}</div>}
                  </div>
                  <span className="phrase-badge" style={{ background: difficultyColor(p.difficulty) }}>
                    {formatLabel(p.difficulty)}
                  </span>
                </div>
                <p className="phrase-card-meaning">{p.meaning}</p>
                <div className="phrase-tags">
                  <span className="phrase-tag phrase-tag-type">{formatLabel(p.type)}</span>
                  <span className="phrase-tag phrase-tag-category">{formatLabel(p.category)}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {totalPages > 1 && (
          <div className="phrase-pagination">
            <button disabled={page === 0} onClick={() => setPage(page - 1)}>← Prev</button>
            <span>Page {page + 1} / {totalPages}</span>
            <button disabled={page + 1 >= totalPages} onClick={() => setPage(page + 1)}>Next →</button>
          </div>
        )}
      </div>
      <Footer />
    </>
  );
};

export default PhraseListPage;
